import * as React from 'react'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import { SecuritySettingsDomainSectionFragment } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteDomainModal } from '../DeleteDomainModal'
import { ShowInstructionsModal } from '../ShowInstructionsModal'
import { useDeleteAuthenticationDomain } from '../useDeleteAuthenticationDomain'
import { useVerifyAuthenticationDomain } from '../useVerifyAuthenticationDomain'
import { DomainStatusLabel } from './DomainStatusLabel'

interface Props {
  domain: SecuritySettingsDomainSectionFragment
  showInstructions: boolean
  onShowInstructions: () => void
  onCloseInstructions: () => void
}

export const DomainListItem = ({
  domain,
  showInstructions,
  onShowInstructions,
  onCloseInstructions,
}: Props) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const { deleteAuthenticationDomain, loading: _ } = useDeleteAuthenticationDomain()
  const { verifyAuthenticationDomain, loading: verifying } = useVerifyAuthenticationDomain()

  const verifyDomain = async (id: number) => {
    const result = await verifyAuthenticationDomain(id)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('security_settings.domain.verify.error'),
        time: 60000,
      })
    })

    if (handled.success) {
      if (!handled.data.domain) return handled

      app.setFlash({
        type: 'correct',
        message: i18n.t('security_settings.domain.verify.success'),
      })
    }
    onCloseInstructions()
    return handled
  }

  const deleteDomain = async (id: number) => {
    const result = await deleteAuthenticationDomain(id)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('security_settings.domain.delete.error'),
        time: 60000,
      })
    })

    if (handled.success) {
      if (!handled.data.domain) return handled

      app.setFlash({
        type: 'correct',
        message: i18n.t('security_settings.domain.delete.success'),
      })
    }
    setShowDeleteModal(false)
    return handled
  }

  const status = domain.status
  return (
    <List.Item key={domain.id}>
      <Text weight="semibold" size="300" color="grey1000">
        {domain.domainName}
      </Text>
      <Box flexDirection="row" gap="s32" alignItems="center">
        <DomainStatusLabel status={status} lastVerifiedAt={domain.lastVerifiedAt} />
        {status !== 'verified' && (
          <Button
            iconName="refresh"
            onClick={() => verifyDomain(domain.id)}
            disabled={verifying}
            type="primary"
          />
        )}
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative
              type="secondary"
              aria-label={i18n.t('security_settings.domain.create.settings')}
              iconName="ellipsis"
            />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {status !== 'verified' && (
              <PopoverMenu.TouchableOption
                title={i18n.t('security_settings.domain.create.verification_token')}
                onClick={onShowInstructions}
              />
            )}
            <PopoverMenu.TouchableOption
              title={i18n.t('common.destroy')}
              danger
              onClick={() => setShowDeleteModal(true)}
            />
          </PopoverMenu.Content>
        </PopoverMenu>
        {showInstructions && (
          <ShowInstructionsModal
            domain={domain}
            onClose={onCloseInstructions}
            onVerify={() => verifyDomain(domain.id)}
          />
        )}
        {showDeleteModal && (
          <DeleteDomainModal
            domain={domain}
            onClose={() => setShowDeleteModal(false)}
            onDelete={() => deleteDomain(domain.id)}
          />
        )}
      </Box>
    </List.Item>
  )
}
